import { create } from "zustand";
import { devtools, persist, subscribeWithSelector } from "zustand/middleware";
import { useShallow } from "zustand/react/shallow";

import type { IUiStore } from "@/stores/uiStore/types";
import { ISystemThemeModeEnum, IThemeModeEnum } from "@/theme/types";
import { APP_ID } from "@/constants/appId";

const name = `${APP_ID}/UI_STORE`;

export const useUiStore = create<IUiStore>()(
  subscribeWithSelector(
    persist(
      devtools(
        (set) => ({
          locale: import.meta.env.VITE_LANGUAGE || "fa",
          changeLocale: (value) => {
            set({ locale: value });
          },
          theme:
            window.matchMedia &&
            window.matchMedia("(prefers-color-scheme: dark)").matches
              ? IThemeModeEnum.Dark
              : IThemeModeEnum.Light,
          setTheme: (value) => {
            set({ theme: value });
          },
          pageTitle: "dashboard.title",
          locatedPath: "",
          pageMode: "create",
          pageType: "inner",
          pageIcon: undefined,
          pageDescriptions: "dashboard.description",
          breadcrumbs: [],
          isPageFullMode: false,
          layoutAction: undefined,
          setting: {
            theme: ISystemThemeModeEnum.System,
            setTheme: (value) => {
              set((data) => ({ setting: { ...data.setting, theme: value } }));
            },
          },
          setLayoutAction: (action) => {
            set({ layoutAction: action });
          },
          setPage(
            request,
            pageTitle,
            pageDescriptions,
            pageMode,
            pageType,
            pageIcon,
            breadcrumbs,
            isPageFullMode,
          ) {
            set({
              pageTitle,
              locatedPath: request?.url,
              pageDescriptions,
              pageMode: pageMode === "index" ? "create" : pageMode,
              pageType,
              pageIcon,
              breadcrumbs,
              isPageFullMode: isPageFullMode || false,
            });
          },
        }),
        {
          enabled: !import.meta.env.PROD,
          name,
        },
      ),
      {
        name,
        partialize: (state) => ({
          // locale: state.locale,
          theme: state.theme,
          setting: {
            theme: state.setting.theme,
            locale: state.locale,
          },
        }),
        merge: (persistedState, currentState): IUiStore => ({
          ...currentState,
          ...(persistedState as Partial<IUiStore>),
          setting: {
            ...currentState.setting,
            ...(persistedState as Partial<IUiStore>)?.setting,
          },
        }),
      },
    ),
  ),
);

export const usePageMode = () => {
  return useUiStore(useShallow((state) => state.pageMode));
};

export const useTheme = () => {
  return useUiStore(useShallow((state) => state.theme));
};
